import type { ChangeEventHandler, FC } from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import debounce from "lodash/debounce";
import { useDispatch } from "react-redux";

import { Table } from "@similarweb/pro-logic-components";
import { TextSwitcherItem } from "@similarweb/ui-components";
import { Stack } from "@similarweb/ui-components/foundations";

import { showSuccessToast } from "app/actions/toast_actions";
import { KeywordsCampaignSwitcherStyled } from "app/components/campaigns/searchEngine/CampaignsSearchEnginesStyledComponents";
import { useDeleteKeywordGroup, useUpdateKeywordGroup } from "app/services/keyword-groups/hooks";
import { keywordGroupsTransforms } from "app/services/keyword-groups/utils/keywordGroupsTransforms";
import { KeywordsGroupEditorModal } from "app/pages/keyword-analysis/KeywordsGroupEditorModal";
import { KeywordGroupsShareModal } from "app/pages/workspace/marketing/pages/KeywordGroupShareModal";
import { allTrackers } from "app/services/track/track";
import { ConfirmationTextModal } from "pro-features/components/Modals/src/ConfirmationTextModal";
import { useTranslation } from "pro-features/components/WithTranslation/src/I18n";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import { FlexRow } from "pro-features/styled components/StyledFlex/src/StyledFlex";

import type { ISharedKeywordGroup } from "app/common/keyword-research/monitor-lists/monitorKeywordsTypes";

import { getColumnConfig } from "./monitor-keyword-groups.table-config";
import type {
    DropdownOnClickProps,
    GroupType,
    IMonitorKeywordsStartPageProps,
    TableData,
} from "./monitorKeywordsStartPageTypes";
import { TabFilter, TabFilterNaming } from "./monitorKeywordsStartPageTypes";
import { MonitorKeywordGroupsTableWrapper, NoDataTableTemplate, SearchInput } from "./styledComponents";

const debouncedTrackEvent = debounce((clb: () => void) => {
    clb();
}, 300);

export const MonitorKeywordsStartPage: FC<IMonitorKeywordsStartPageProps> = ({
    keywordGroups = [],
    sharedKeywordGroups = [],
    users,
    navigationTargets,
}) => {
    const translate = useTranslation();
    const [searchText, setSearchText] = useState<string>("");
    const [tabIndex, setTabIndex] = useState<number>(TabFilter.ALL);
    const [currentGroupEdit, setCurrentGroupEdit] = useState<GroupType | undefined>(undefined);
    const [groupToShare, setGroupToShare] = useState<GroupType | undefined>(undefined);
    const [groupToDelete, setGroupToDelete] = useState<GroupType | undefined>(undefined);

    const { mutate: deleteKeywordGroup, isPending: isDeleteLoading } = useDeleteKeywordGroup();
    const { mutate: updateKeywordGroup, isPending: isUpdateLoading } = useUpdateKeywordGroup();
    const isLoading = isDeleteLoading || isUpdateLoading;

    const latestGroupsRef = useRef<{ keywordGroups: GroupType[]; sharedKeywordGroups: GroupType[] }>({
        keywordGroups,
        sharedKeywordGroups,
    });

    useEffect(() => {
        latestGroupsRef.current = { keywordGroups, sharedKeywordGroups };
    }, [keywordGroups, sharedKeywordGroups]);

    const [sorting, setSorting] = useState<{
        dataIndex: keyof ISharedKeywordGroup;
        sortDirection: "ascend" | "descend";
    }>({
        dataIndex: "LastUpdated",
        sortDirection: "descend",
    });

    const handleSortBy = useCallback((dataIndex: keyof ISharedKeywordGroup, sortDirection: "ascend" | "descend") => {
        setSorting({ dataIndex, sortDirection });
        allTrackers.dispatchTrackEvent("Sort", "Click", dataIndex);
    }, []);

    const dispatch = useDispatch();

    const tableData = useMemo(() => {
        let groups: TableData = [];

        switch (tabIndex) {
            case TabFilter.MY:
                groups = keywordGroups;
                break;
            case TabFilter.SHARED:
                groups = sharedKeywordGroups;
                break;
            default:
                groups = keywordGroups.concat(sharedKeywordGroups);
        }

        if (!searchText) {
            return groups;
        }

        return groups.filter((group) => group.Name.toLowerCase().includes(searchText.toLowerCase()));
    }, [tabIndex, searchText, keywordGroups, sharedKeywordGroups]);

    /** methods for handling of sharing an existing group */
    const onShareClick = useCallback(
        (group: GroupType) => {
            const { keywordGroups: latestKeywordGroups, sharedKeywordGroups: latestSharedKeywordGroups } =
                latestGroupsRef.current;

            // The sharing modal component assumes all the object keys are camelCase, therefore, we camelCase the keys.
            const transformedGroupDataForSharing = keywordGroupsTransforms.adaptGroupDataForShareModal(group, [
                ...latestKeywordGroups,
                ...latestSharedKeywordGroups,
            ]);
            setGroupToShare(transformedGroupDataForSharing);
            allTrackers.dispatchTrackEvent("Drop Down", "Click", "Share/Share icon");
        },
        [latestGroupsRef],
    );

    // callback called by the keywordService after share request is complete
    const onFinishSharing = () => {
        setGroupToShare(undefined);
    };

    const onDeleteModalCloseClick = () => {
        setGroupToDelete(undefined);
    };

    const onDeleteModalConfirmDeleteClick = () => {
        deleteKeywordGroup(
            { group: groupToDelete },
            {
                onSuccess: () => {
                    dispatch(showSuccessToast(translate("delete.list.wizard.toast", { listName: groupToDelete.Name })));
                },
            },
        );
        setGroupToDelete(undefined);
    };

    const handleSave = (modifiedGroup: GroupType) => {
        updateKeywordGroup(modifiedGroup);
    };

    const handleSearchChange: ChangeEventHandler<HTMLInputElement> = (event) => {
        if (event.target.value.trim()) {
            debouncedTrackEvent(() => allTrackers.dispatchTrackEvent("Search Bar", "Type", "Search"));
        }
        setSearchText(event.target.value.trim());
    };

    const handleSwitchItemChange = (itemIndex: number) => {
        allTrackers.dispatchTrackEvent("Tab", "click", `Change page tab/${TabFilterNaming[itemIndex]}`);
        setTabIndex(itemIndex);
    };

    const handleEditKeywordsGroups = useCallback((groupItem: GroupType) => {
        allTrackers.dispatchTrackEvent("Drop Down", "Click", "Edit");
        setCurrentGroupEdit(groupItem);
    }, []);

    const navigateToMonitorKeywords = useCallback(
        (record: GroupType) => {
            void proNavigator.go(navigationTargets.monitorKeywordsState, {
                keywordGroupId: record.Id,
                duration: "12m",
                country: 999,
                webSource: "Total",
                keywordsType: "both",
            });
        },
        [navigationTargets.monitorKeywordsState],
    );

    const navigateToKeywordAnalysis = useCallback(
        (record: GroupType) => {
            void proNavigator.go(navigationTargets.serpPlayersState, {
                duration: "12m",
                country: 999,
                keyword: `*${record.Id}`,
                webSource: "Total",
            });
        },
        [navigationTargets.serpPlayersState],
    );

    const navigateToTrafficAnalysis = useCallback(
        (record: GroupType) => {
            void proNavigator.go(navigationTargets.trafficAnalysisState, {
                duration: "12m",
                country: 999,
                keyword: `*${record.Id}`,
                webSource: "Total",
            });
        },
        [navigationTargets.trafficAnalysisState],
    );

    const handleDropdownClick = useCallback(
        ({ id }: DropdownOnClickProps, row: GroupType) => {
            switch (id) {
                case "openListPage":
                    allTrackers.dispatchTrackEvent("Drop Down", "Click", "Open Report/Keyword list page/Dropdown");
                    navigateToMonitorKeywords(row);
                    break;
                case "openSerpPage":
                    allTrackers.dispatchTrackEvent("Drop Down", "Click", "Open Report/Serp page");
                    navigateToKeywordAnalysis(row);
                    break;
                case "openPagesPage":
                    allTrackers.dispatchTrackEvent("Drop Down", "Click", "Open Report/Pages page");
                    navigateToTrafficAnalysis(row);
                    break;
                case "edit":
                    allTrackers.dispatchTrackEvent("Drop Down", "Click", "Edit/More Actions");
                    handleEditKeywordsGroups(row);
                    break;
                case "share":
                    allTrackers.dispatchTrackEvent("Drop Down", "Click", "Share/More Actions");
                    onShareClick(row);
                    break;
                case "delete":
                    allTrackers.dispatchTrackEvent("Drop Down", "Click", "Delete");
                    setGroupToDelete(row);
                    break;
                default:
                    console.error(`Unknown dropdown item id: ${id as string}`);
            }
        },
        [
            handleEditKeywordsGroups,
            navigateToKeywordAnalysis,
            navigateToMonitorKeywords,
            navigateToTrafficAnalysis,
            onShareClick,
        ],
    );

    const tableColumnConfig = useMemo(() => {
        return getColumnConfig({
            handleEditKeywordsGroups,
            onShareClick,
            handleDropdownClick,
            tableRowCount: tableData?.length ?? 0,
            sorting,
        });
    }, [handleDropdownClick, sorting, tableData?.length, handleEditKeywordsGroups, onShareClick]);

    return (
        <Stack gap={tableData?.length === 0 ? "100px" : "24px"}>
            <FlexRow gap="16px">
                {/* Wrapper for KeywordsCampaignSwitcherStyled, design breaks if nested inside of a flex */}
                <div id="keyword-lists-switch-wrapper">
                    <KeywordsCampaignSwitcherStyled
                        selectedIndex={tabIndex}
                        onItemClick={handleSwitchItemChange}
                    >
                        <TextSwitcherItem
                            data-automation-button-name="kwl-all"
                            key="all"
                        >
                            {translate("global.all")}
                        </TextSwitcherItem>
                        <TextSwitcherItem
                            data-automation-button-name="kwl-my"
                            key="my"
                        >
                            {translate("global.my.lists")}
                        </TextSwitcherItem>
                        <TextSwitcherItem
                            data-automation-button-name="kwl-shared"
                            key="shared"
                        >
                            {translate("global.shared.lists")}
                        </TextSwitcherItem>
                    </KeywordsCampaignSwitcherStyled>
                </div>
                <SearchInput
                    placeholder="Search for keyword lists"
                    onChange={handleSearchChange}
                    data-automation-name="kwl-search"
                />
            </FlexRow>
            {tableData?.length === 0 ? (
                <NoDataTableTemplate
                    tab={tabIndex}
                    filterActive={!!searchText}
                />
            ) : (
                <MonitorKeywordGroupsTableWrapper>
                    <Table
                        id="monitor-keyword-groups-table"
                        size="large"
                        pagination={{
                            pageSize: 100,
                        }}
                        onSort={(sorterData) => {
                            handleSortBy(sorterData.columnKey as keyof ISharedKeywordGroup, sorterData.order);
                        }}
                        sticky={false}
                        columnConfig={tableColumnConfig}
                        data={tableData}
                        theme="carbon"
                        isLoading={isLoading}
                        onRow={(record) => ({
                            onClick: (_event) => {
                                navigateToMonitorKeywords(record);
                            },
                        })}
                        noData={{
                            title: "No keyword lists found",
                            subtitle: "Create a new list to get started",
                        }}
                    />
                </MonitorKeywordGroupsTableWrapper>
            )}
            <KeywordsGroupEditorModal
                onClose={() => setCurrentGroupEdit(undefined)}
                open={Boolean(currentGroupEdit)}
                keywordsGroup={currentGroupEdit}
                onEdit={handleSave}
                onDelete={onDeleteModalConfirmDeleteClick}
                showGoToMonitorKeywordListButton
            />
            {/* Although the modal is not shown, the component is still rendered and expects logic that is yet ready */}
            {groupToShare ? (
                <KeywordGroupsShareModal
                    keywordGroup={groupToShare}
                    isOpen={Boolean(groupToShare)}
                    users={users ?? []}
                    onCloseClick={() => setGroupToShare(undefined)}
                    onFinish={onFinishSharing}
                />
            ) : null}
            <ConfirmationTextModal
                isOpen={Boolean(groupToDelete)}
                onCloseClick={onDeleteModalCloseClick}
                onCancelClick={onDeleteModalCloseClick}
                onApproveClick={onDeleteModalConfirmDeleteClick}
                cancelButtonText={translate("workspaces.grouprow.confirmaion.cancel.button")}
                approveButtonText={translate("workspaces.grouprow.confirmaion.ok.button")}
                headerText={translate("workspaces.marketing.ppc.grouprow.confirmation.header.text", {
                    name: groupToDelete?.Name,
                })}
                contentText={translate("workspaces.marketing.ppc.grouprow.confirmation.content.text")}
            />
        </Stack>
    );
};
